import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { getRole } from "../../helper";
import FactoryStockService from "../../services/FactoryStockService";


const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};


// =====================================================
// COMPONENT
// =====================================================

const DeviceFeatureManagement = () => {
  const role = getRole();

  const normalizedRole = role
    ?.toString()
    .trim()
    .toLowerCase();


  const isSuperAdmin =
    normalizedRole === "superadmin" 

  const isFactoryManager =
    normalizedRole === "factorymanager"

  const [features, setFeatures] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);


  // ===================================================
  // FORM
  // ===================================================

  const [formOpen, setFormOpen] = useState(false);

  const [editingFeature, setEditingFeature] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    description: "",
  });

  const [saving, setSaving] = useState(false);


  // ===================================================
  // DELETE
  // ===================================================

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedFeature, setSelectedFeature] = useState(null);

  const [deleting, setDeleting] = useState(false);


  // ===================================================
  // SNACKBAR
  // ===================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  // ===================================================
  // SNACKBAR HELPER
  // ===================================================

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };


  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  // ===================================================
  // EXTRACT API ERROR
  // ===================================================

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;

    if (!data) {
      return fallback;
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.error) {
      return data.error;
    }

    if (data.detail) {
      return data.detail;
    }

    // Serializer errors
    if (typeof data === "object") {
      const messages = [];

      Object.entries(data).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          messages.push(`${field}: ${value.join(", ")}`);
        } else if (typeof value === "string") {
          messages.push(`${field}: ${value}`);
        }
      });

      if (messages.length > 0) {
        return messages.join(" | ");
      }
    }

    return fallback;
  };


  // ===================================================
  // LOAD FEATURES
  // ===================================================

  const loadFeatures = async () => {
    setLoading(true);

    try {
      const response =
        await FactoryStockService.deviceFeatureList({
          include_deleted:
            isSuperAdmin
              ? includeDeleted
              : false,
        });

      const responseData = response?.data;

      let list = [];

      if (Array.isArray(responseData)) {
        list = responseData;
      } else if (Array.isArray(responseData?.results)) {
        list = responseData.results;
      } else if (Array.isArray(responseData?.data)) {
        list = responseData.data;
      }

      setFeatures(list);

      setPage(0);
    } catch (error) {
      console.error("Device feature list error:", error);

      showSnackbar(
        getApiErrorMessage(
          error,
          "Failed to load device features."
        ),
        "error"
      );

      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadFeatures();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);


  // ===================================================
  // FILTER
  // ===================================================

  const filteredFeatures = useMemo(() => {
    const searchValue = search
      .trim()
      .toLowerCase();

    if (!searchValue) {
      return features;
    }

    return features.filter((feature) => {
      return (
        feature?.name
          ?.toLowerCase()
          .includes(searchValue) ||
        feature?.description
          ?.toLowerCase()
          .includes(searchValue)
      );
    });
  }, [features, search]);


  // ===================================================
  // PAGINATION
  // ===================================================

  const paginatedFeatures = useMemo(() => {
    const start =
      page * rowsPerPage;

    return filteredFeatures.slice(
      start,
      start + rowsPerPage
    );
  }, [
    filteredFeatures,
    page,
    rowsPerPage,
  ]);


  // ===================================================
  // OPEN CREATE
  // ===================================================

  const handleOpenCreate = () => {
    setEditingFeature(null);

    setFormData({
      name: "",
      description: "",
    });

    setFormErrors({
      name: "",
      description: "",
    });

    setFormOpen(true);
  };


  // ===================================================
  // OPEN EDIT
  // ===================================================

  const handleOpenEdit = (feature) => {
    setEditingFeature(feature);

    setFormData({
      name: feature?.name || "",
      description:
        feature?.description || "",
    });

    setFormErrors({
      name: "",
      description: "",
    });

    setFormOpen(true);
  };


  // ===================================================
  // CLOSE FORM
  // ===================================================

  const handleCloseForm = () => {
    if (saving) return;

    setFormOpen(false);

    setEditingFeature(null);

    setFormData({
      name: "",
      description: "",
    });

    setFormErrors({
      name: "",
      description: "",
    });
  };


  // ===================================================
  // FORM CHANGE
  // ===================================================

  const handleFormChange = (event) => {
    const { name, value } =
      event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };


  // ===================================================
  // VALIDATE
  // ===================================================

  const validateForm = () => {
    const errors = {
      name: "",
      description: "",
    };

    const name =
      formData.name.trim();

    if (!name) {
      errors.name =
        "Feature name is required.";
    }

    if (name.length > 255) {
      errors.name =
        "Feature name cannot exceed 255 characters.";
    }

    setFormErrors(errors);

    return !Object.values(errors).some(
      Boolean
    );
  };


  // ===================================================
  // CREATE / UPDATE
  // ===================================================

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    const payload = {
      name: formData.name.trim(),
      description:
        formData.description.trim() || null,
    };

    try {
      if (editingFeature) {
        // =============================================
        // UPDATE
        // =============================================

        await FactoryStockService.updateDeviceFeature({
          feature_id:
            editingFeature.id,
          ...payload,
        });

        showSnackbar(
          "Device feature updated successfully.",
          "success"
        );
      } else {
        // =============================================
        // CREATE
        // =============================================

        await FactoryStockService.createDeviceFeature(
          payload
        );

        showSnackbar(
          "Device feature created successfully.",
          "success"
        );
      }

      handleCloseForm();

      await loadFeatures();
    } catch (error) {
      console.error(
        "Device feature save error:",
        error
      );

      showSnackbar(
        getApiErrorMessage(
          error,
          editingFeature
            ? "Failed to update device feature."
            : "Failed to create device feature."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };


  // ===================================================
  // OPEN DELETE CONFIRMATION
  // ===================================================

  const handleOpenDelete = (feature) => {
    setSelectedFeature(feature);

    setDeleteDialogOpen(true);
  };


  // ===================================================
  // CLOSE DELETE
  // ===================================================

  const handleCloseDelete = () => {
    if (deleting) return;

    setDeleteDialogOpen(false);

    setSelectedFeature(null);
  };


  // ===================================================
  // SOFT DELETE
  // ===================================================

  const handleDelete = async () => {
    if (!selectedFeature?.id) {
      return;
    }

    setDeleting(true);

    try {
      await FactoryStockService.softDeleteDeviceFeature({
        feature_id:
          selectedFeature.id,
      });

      showSnackbar(
        "Device feature deleted successfully.",
        "success"
      );

      handleCloseDelete();

      await loadFeatures();
    } catch (error) {
      console.error(
        "Device feature delete error:",
        error
      );

      showSnackbar(
        getApiErrorMessage(
          error,
          "Failed to delete device feature."
        ),
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };


  // ===================================================
  // PAGINATION CHANGE
  // ===================================================

  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };


  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(
        event.target.value,
        10
      )
    );

    setPage(0);
  };


  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
      }}
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Device Feature Management
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Manage device features used
              for stock configuration.
            </Typography>
          </Box>

          {isSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={
                handleOpenCreate
              }
            >
              Add Device Feature
            </Button>
          )}
        </Box>

        <Divider />

        {/* =================================================
            TOOLBAR
        ================================================= */}

        {/* <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search feature..."
            value={search}
            onChange={(event) => {
              setSearch(
                event.target.value
              );
              setPage(0);
            }}
            sx={{
              minWidth: 280,
              flex: 1,
              maxWidth: 450,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                  />
                </InputAdornment>
              ),
            }}
          />

          {isSuperAdmin && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Switch
                checked={includeDeleted}
                onChange={(event) =>
                  setIncludeDeleted(
                    event.target.checked
                  )
                }
              />

              <Typography
                variant="body2"
              >
                Show deleted
              </Typography>
            </Stack>
          )}

          <Tooltip title="Refresh">
            <IconButton
              onClick={loadFeatures}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  size={20}
                />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box> */}


        {/* =================================================
            TABLE
        ================================================= */}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  #
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Feature Name
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Description
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Status
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Created At
                </TableCell>

                {isSuperAdmin && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      isSuperAdmin
                        ? 6
                        : 5
                    }
                    align="center"
                    sx={{
                      py: 6,
                    }}
                  >
                    <CircularProgress
                      size={30}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                      }}
                    >
                      Loading device
                      features...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedFeatures.length ===
                0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      isSuperAdmin
                        ? 6
                        : 5
                    }
                    align="center"
                    sx={{
                      py: 6,
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={500}
                    >
                      No device features
                      found
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                      }}
                    >
                      {search
                        ? "Try changing your search."
                        : "No device features are available."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFeatures.map(
                  (feature, index) => (
                    <TableRow
                      key={feature.id}
                      hover
                    >
                      <TableCell>
                        {page *
                          rowsPerPage +
                          index +
                          1}
                      </TableCell>

                      <TableCell>
                        <Typography
                          fontWeight={500}
                        >
                          {feature.name ||
                            "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 400,
                            whiteSpace:
                              "normal",
                            wordBreak:
                              "break-word",
                          }}
                        >
                          {feature.description ||
                            "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {feature.is_deleted ? (
                          <Chip
                            label="Deleted"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        {formatDate(
                          feature.created_at
                        )}
                      </TableCell>

                      {isSuperAdmin && (
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={0.5}
                          >
                            {/* EDIT */}
                            <Tooltip title="Edit">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={
                                    feature.is_deleted
                                  }
                                  onClick={() =>
                                    handleOpenEdit(
                                      feature
                                    )
                                  }
                                >
                                  <EditIcon
                                    fontSize="small"
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* DELETE */}
                            <Tooltip title="Soft Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={
                                    feature.is_deleted
                                  }
                                  onClick={() =>
                                    handleOpenDelete(
                                      feature
                                    )
                                  }
                                >
                                  <DeleteOutlineIcon
                                    fontSize="small"
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>


        {/* =================================================
            PAGINATION
        ================================================= */}

        <TablePagination
          component="div"
          count={
            filteredFeatures.length
          }
          page={page}
          onPageChange={
            handleChangePage
          }
          rowsPerPage={
            rowsPerPage
          }
          onRowsPerPageChange={
            handleChangeRowsPerPage
          }
          rowsPerPageOptions={[
            5,
            10,
            25,
            50,
          ]}
        />
      </Paper>


      {/* =================================================
          CREATE / UPDATE DIALOG
      ================================================= */}

      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
            >
              {editingFeature
                ? "Edit Device Feature"
                : "Add Device Feature"}
            </Typography>

            <IconButton
              onClick={handleCloseForm}
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            pt: 3,
          }}
        >
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              required
              label="Feature Name"
              name="name"
              value={formData.name}
              onChange={
                handleFormChange
              }
              error={
                Boolean(
                  formErrors.name
                )
              }
              helperText={
                formErrors.name ||
                "Feature name must be unique."
              }
              disabled={saving}
              autoFocus
            />

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description"
              name="description"
              value={
                formData.description
              }
              onChange={
                handleFormChange
              }
              helperText="Optional free-text notes."
              disabled={saving}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCloseForm}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              saving ||
              !formData.name.trim()
            }
            startIcon={
              saving ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : null
            }
          >
            {saving
              ? "Saving..."
              : editingFeature
              ? "Update Feature"
              : "Create Feature"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          DELETE CONFIRMATION
      ================================================= */}

      <Dialog
        open={deleteDialogOpen}
        onClose={
          handleCloseDelete
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Device Feature?
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Are you sure you want to
            soft-delete this device
            feature?
          </Typography>

          {selectedFeature && (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor:
                  "action.hover",
              }}
            >
              <Typography
                fontWeight={600}
              >
                {selectedFeature.name}
              </Typography>

              {selectedFeature.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {
                    selectedFeature.description
                  }
                </Typography>
              )}
            </Paper>
          )}

          <Alert
            severity="warning"
            sx={{ mt: 2 }}
          >
            This is a soft delete. Existing
            stock assignments will continue
            to reference this feature.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDelete}
            disabled={deleting}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <DeleteOutlineIcon />
              )
            }
          >
            {deleting
              ? "Deleting..."
              : "Soft Delete"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={
            snackbar.severity
          }
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceFeatureManagement;
